import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import KeyboardReturnOutlinedIcon from '@mui/icons-material/KeyboardReturnOutlined'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import RemoveShoppingCartOutlinedIcon from '@mui/icons-material/RemoveShoppingCartOutlined'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined'
import DashboardIcon from '@/components/Svg/school/menu/dashboard'
import getEnvVar from '@/lib/getEnvVar'
import { MENU_GROUPS } from '@/lib/routes/menuGroups'
import { canSeeErpGuide } from '@/lib/store/erpGuide'
import { EntityList } from '@/types/Entity'
import { CONSTANTS } from '@/types/enum/constants'

// Espace vendeur : actif en dev ; en production, ouvert vendeur par vendeur par l'admin dans
// « Gestion Store » (`storeExtranetBeta`, lu dans le profil), visible mais grisé pour les autres.
// Tant que l'activation globale (retrait de `disabled`) n'est pas faite.
const isProduction = getEnvVar('APP_ENV') === 'production'
const isVendorBeta = (dataMyProfile?: any): boolean =>
  !!dataMyProfile?.selectCompanyDefault?.storeExtranetBeta
const storeDisabled = ({ dataMyProfile }: { dataMyProfile?: any }): boolean =>
  isProduction && !isVendorBeta(dataMyProfile)
const DISABLED_REASON_T = 'routes.industryoffice.store.comingSoon'

// Acces a l'espace vendeur Store = deux faits, sans flag d'activation supplementaire :
//   1. le user porte le role vendeur       -> deja exige par `roles` sur chaque entree
//   2. sa company est un vendeur boutique  -> `companyIdStore` renseigne par la synchro
// Meme regle que fnDisplayIndustryVendorNav (entrees « parite vendeur » cote espace labo).
const fnDisplayStoreVendor =
  (base?: (args: any) => boolean) =>
  (args: { dataMyProfile?: any; menuBadges?: any }): boolean => {
    if (base && !base(args)) return false

    return !!args.dataMyProfile?.selectCompanyDefault?.companyIdStore
  }

const getIndustryOfficeStoreIndustry = [
  {
    route: 'Dashboard',
    text: 'Tableau de bord',
    icon: DashboardIcon,
    href: '/industryoffice/store/dashboard-industry',
    menuHeader: true,
    position: 10,
    menuFlat: true,
  },
  {
    route: 'MyProfile',
    text: 'Mon profil',
    icon: PersonOutlineOutlinedIcon,
    href: '/backoffice/common/my-profile',
    menuHeader: true,
    position: 400,
  },
  {
    route: 'Accounting',
    text: 'Comptabilité',
    t: 'routes.industryoffice.store.accounting',
    icon: CalculateOutlinedIcon,
    href: '/industryoffice/store/accounting',
    menuHeader: true,
    position: 116,
    menuGroup: MENU_GROUPS.VENDOR_RESULTS,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  {
    route: 'Messages',
    text: 'Messages',
    t: 'routes.industryoffice.store.messages',
    icon: ForumOutlinedIcon,
    href: '/industryoffice/store/messages',
    menuHeader: true,
    position: 103,
    menuFlat: true,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  {
    route: 'Disputes',
    text: 'Mes litiges',
    t: 'routes.industryoffice.store.disputes',
    icon: GavelOutlinedIcon,
    href: '/industryoffice/store/disputes',
    menuHeader: true,
    position: 104,
    menuFlat: true,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  {
    route: 'Promotions',
    text: 'Mes promotions',
    t: 'routes.industryoffice.store.promotions',
    icon: LocalOfferOutlinedIcon,
    href: '/industryoffice/store/promotions',
    menuHeader: true,
    position: 109,
    menuGroup: MENU_GROUPS.VENDOR_CATALOG,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  {
    route: 'Orders',
    text: 'Mes commandes',
    t: 'routes.industryoffice.store.orders',
    icon: ShoppingCartOutlinedIcon,
    href: '/industryoffice/store/orders',
    menuHeader: true,
    position: 101,
    menuFlat: true,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  // Reprise LAP, lot 6 bis : le referentiel est la boutique ; « Mes produits » en est la vue (plus
  // d'entree « Referentiel »). « Laboratoires et marques » a son entree, sous la meme condition que
  // l'ecran (connexion ERP dont la lecture du referentiel est ouverte), calculee par le serveur.
  {
    route: 'VendorLaboratories',
    text: 'Laboratoires, marques et planning',
    t: 'routes.industryoffice.store.laboratories',
    icon: InventoryOutlinedIcon,
    href: '/industryoffice/store/catalog/laboratories',
    menuHeader: true,
    position: 107,
    menuGroup: MENU_GROUPS.VENDOR_CATALOG,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
    fnDisplay: ({ dataMyProfile }: { dataMyProfile?: any }) =>
      !!dataMyProfile?.selectCompanyDefault?.erpCatalogManaged,
  },
  // Reprise LAP : tableau de bord logistique (quoi faire aujourd'hui, cycle outre-mer, stock, laboratoires).
  // Lecture seule ; même condition que le guide LAP (vendeur relié à son ERP, calculée par le serveur).
  {
    route: 'VendorLogistics',
    text: 'Pilotage logistique',
    t: 'routes.industryoffice.store.logistics',
    icon: FactCheckOutlinedIcon,
    href: '/industryoffice/store/logistics',
    menuHeader: true,
    position: 100,
    menuFlat: true,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
    fnDisplay: ({ dataMyProfile }: { dataMyProfile?: any }) => canSeeErpGuide(dataMyProfile),
  },
  // Reprise LAP, lot 4 : consolidation et redescente des commandes Aelia / OutreMer. Le menu
  // n'apparait que pour le vendeur operateur (drapeau calcule par le serveur), comme l'ecran.
  {
    route: 'VendorOverseasOrders',
    text: 'Outre-mer',
    t: 'routes.industryoffice.store.overseas',
    icon: PublicOutlinedIcon,
    href: '/industryoffice/store/overseas',
    menuHeader: true,
    position: 102,
    menuFlat: true,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
    fnDisplay: ({ dataMyProfile }: { dataMyProfile?: any }) =>
      !!dataMyProfile?.selectCompanyDefault?.erpOverseasOperator,
  },
  // Reprise LAP : stock par emplacement et répartition France / outre-mer, lus dans Odoo (lecture
  // seule). Même condition que les autres écrans LAP (vendeur relié à son ERP, calculé par le serveur).
  {
    route: 'VendorStock',
    text: 'Stock et emplacements',
    t: 'routes.industryoffice.store.stock',
    icon: WarehouseOutlinedIcon,
    href: '/industryoffice/store/stock',
    menuHeader: true,
    position: 106,
    menuGroup: MENU_GROUPS.VENDOR_CATALOG,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
    fnDisplay: ({ dataMyProfile }: { dataMyProfile?: any }) => canSeeErpGuide(dataMyProfile),
  },
  {
    route: 'PlanningShippings',
    text: 'Planning expéditions',
    t: 'routes.industryoffice.store.planningShippings',
    icon: EventBusyOutlinedIcon,
    href: '/industryoffice/store/planning-shippings',
    menuHeader: true,
    position: 108,
    menuGroup: MENU_GROUPS.VENDOR_CATALOG,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
    // Vendeur dont le referentiel est suivi : le planning est un onglet de « Laboratoires, marques et
    // planning » (reprise LAP, lot 6 bis), pas une entree a part.
    fnDisplay: ({ dataMyProfile }: { dataMyProfile?: any }) =>
      !dataMyProfile?.selectCompanyDefault?.erpCatalogManaged,
  },
  {
    route: 'Products',
    text: 'Mes produits',
    t: 'routes.industryoffice.store.products',
    icon: Inventory2OutlinedIcon,
    href: '/industryoffice/store/products',
    menuHeader: true,
    position: 105,
    menuGroup: MENU_GROUPS.VENDOR_CATALOG,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  {
    route: 'Shippings',
    text: 'Modes de livraison',
    t: 'routes.industryoffice.store.shippings',
    icon: LocalShippingOutlinedIcon,
    href: '/industryoffice/store/shippings',
    menuHeader: true,
    position: 123,
    menuGroup: MENU_GROUPS.VENDOR_ACCOUNT,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  // Parite panneau vendeur CS-Cart (regle vendor-parity-store-industry) : memes ecrans
  // que l'espace labo, mais scopes sur SA boutique cote serveur.
  {
    route: 'VendorStatistics',
    text: 'Statistiques',
    t: 'routes.industryoffice.store.statistics',
    icon: QueryStatsOutlinedIcon,
    href: '/industryoffice/store/statistics',
    menuHeader: true,
    position: 115,
    menuGroup: MENU_GROUPS.VENDOR_RESULTS,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  {
    route: 'VendorLgoRefusals',
    text: 'Refus LGO',
    t: 'routes.industryoffice.store.refusLgo',
    icon: RemoveShoppingCartOutlinedIcon,
    href: '/industryoffice/store/refus-lgo',
    menuHeader: true,
    position: 117,
    menuGroup: MENU_GROUPS.VENDOR_RESULTS,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
    // Hors du circuit LGO, l'écran n'a pas d'objet. La boutique seule le sait : `null` (source
    // indisponible, ou pas encore lue) garde l'entrée, l'écran affiche alors son état dédié.
    fnDisplay: ({ menuBadges }: { menuBadges?: { storeLgoVendor?: boolean | null } }) =>
      menuBadges?.storeLgoVendor !== false,
  },
  {
    route: 'GuidesDocumentations',
    text: 'Aide',
    t: 'routes.industryoffice.store.help',
    icon: MenuBookOutlinedIcon,
    href: '/industryoffice/store/documentation',
    menuHeader: true,
    position: 210,
    menuFlat: 'bottom' as const,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  // Reprise LAP : guide d'utilisation des écrans reliés à l'ERP (copie du guide du dépôt racine,
  // `yarn lap:guide`). Onglet de « Aide » ; la route reste pour le lien de la notice du mode ombre.
  {
    route: 'VendorErpGuide',
    text: 'Guide d’utilisation LAP',
    t: 'routes.industryoffice.store.erpGuide',
    href: '/industryoffice/store/erp-guide',
    menuHeader: false,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
    fnDisplay: ({ dataMyProfile }: { dataMyProfile?: any }) => canSeeErpGuide(dataMyProfile),
  },
  {
    route: 'Contrat',
    text: 'Contrat',
    icon: DescriptionOutlinedIcon,
    href: '/industryoffice/store/contract',
    menuHeader: true,
    position: 122,
    menuGroup: MENU_GROUPS.VENDOR_ACCOUNT,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  {
    route: 'StripeAccount',
    text: 'Compte de paiement',
    t: 'routes.industryoffice.store.stripeAccount',
    icon: AccountBalanceWalletOutlinedIcon,
    href: '/industryoffice/store/payments',
    menuHeader: true,
    position: 120,
    menuGroup: MENU_GROUPS.VENDOR_ACCOUNT,
  },
  {
    route: 'Sales',
    text: 'Abonnements et Factures achats',
    t: 'routes.frontoffice.all.sales',
    icon: ReceiptLongOutlinedIcon,
    href: '/frontoffice/group/billing/sales-invoices',
    menuHeader: true,
    position: 121,
    menuGroup: MENU_GROUPS.VENDOR_ACCOUNT,
    isMobile: false,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
    // France uniquement : porte par ENTITY_COUNTRIES[PHARMEDISTORE] via l'entite de la
    // route — ne pas le redire ici (cf. lib/country/entityCountries).
  },
  {
    route: 'BackToOldVersion',
    text: 'Revenir à l’ancienne version',
    t: 'routes.industryoffice.store.backToOldVersion',
    icon: KeyboardReturnOutlinedIcon,
    href: '/industryoffice/store/exit-beta',
    menuHeader: true,
    position: 500,
    menuGroup: MENU_GROUPS.VENDOR_ACCOUNT,
    // Visible uniquement pour un vendeur en mode beta (nouvelle version active)
    fnDisplay: ({ dataMyProfile }: { dataMyProfile?: any }) => isVendorBeta(dataMyProfile),
  },
  // Pages de détail / création / édition (hors menu) — déclarées pour l'autorisation
  // de route (withAuthent matche href === router.pathname) ; sinon redirection dashboard.
  { route: 'OrderDetail', text: 'Commande', href: '/industryoffice/store/orders/[orderId]', menuHeader: false },
  // Détail d'un montant du solde (loupes de la page Comptabilité), même ouverture que celle-ci
  {
    route: 'AccountingDetail',
    text: 'Détail du solde',
    t: 'routes.industryoffice.store.accountingDetail',
    href: '/industryoffice/store/accounting/[bucket]',
    menuHeader: false,
    disabled: storeDisabled,
    disabledReasonT: DISABLED_REASON_T,
  },
  {
    route: 'VendorOverseasSupply',
    text: 'Besoins outre-mer',
    href: '/industryoffice/store/overseas/supply',
    menuHeader: false,
  },
  {
    route: 'VendorOverseasOrderDetail',
    text: 'Commande outre-mer',
    href: '/industryoffice/store/overseas/[overseasOrderId]',
    menuHeader: false,
  },
  { route: 'NewProduct', text: 'Nouveau produit', href: '/industryoffice/store/products/new', menuHeader: false },
  { route: 'EditProduct', text: 'Modifier le produit', href: '/industryoffice/store/products/edit/[productId]', menuHeader: false },
  { route: 'ImportProducts', text: 'Importer des produits', href: '/industryoffice/store/products/import', menuHeader: false },
  { route: 'NewPromotion', text: 'Nouvelle promotion', href: '/industryoffice/store/promotions/new', menuHeader: false },
  { route: 'EditPromotion', text: 'Modifier la promotion', href: '/industryoffice/store/promotions/edit/[promotionId]', menuHeader: false },
  { route: 'NewPlanning', text: 'Nouveau planning', href: '/industryoffice/store/planning-shippings/new', menuHeader: false },
  { route: 'EditPlanning', text: 'Modifier le planning', href: '/industryoffice/store/planning-shippings/edit/[planningId]', menuHeader: false },
  { route: 'DisputeDetail', text: 'Litige', href: '/industryoffice/store/disputes/[disputeId]', menuHeader: false },
  { route: 'MessageThread', text: 'Conversation', href: '/industryoffice/store/messages/[threadId]', menuHeader: false },
  { route: 'NewMessage', text: 'Nouveau message', href: '/industryoffice/store/messages/new', menuHeader: false },
].map(route => ({
  ...route,
  entity: [EntityList.PHARMEDISTORE],
  roles: [CONSTANTS.ROLE_DYNAMIC_ROUTE_INDUSTRY_USER_STORE_VENDOR],
  // Role vendeur (ci-dessus) ET company rattachee a un vendeur boutique (ci-dessous).
  fnDisplay: fnDisplayStoreVendor((route as { fnDisplay?: (args: any) => boolean }).fnDisplay),
}))

export default getIndustryOfficeStoreIndustry
